import { PropsWithChildren, useEffect, useState } from 'react'
import { LOGIN_URL_RESPONSE_TYPE, LOGIN_URL_SCOPE } from '../utils/constants'
import { RootState, useAppSelector } from '../utils/agent.store'
import LoginAttempt from '../components/common/LoginAttempt'

const RedirectLoginUrl: React.FC<{ url: string }> = ({ url }) => {
  useEffect(() => {
    window.location.href = url
  }, [url])
  return <LoginAttempt token={null} text={'Redirecting for login...'} />
}

const CognitoAuthGuard: React.FC<
  PropsWithChildren<{ loginURL: string; loginClientId: string }>
> = ({ loginClientId, loginURL, children }) => {
  const responseType = LOGIN_URL_RESPONSE_TYPE
  const scope = encodeURIComponent(LOGIN_URL_SCOPE)
  const redirectUri = encodeURIComponent(window.location.origin)
  const [loginError, setLoginError] = useState<string>('')
  const [searchParams] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params
  })
  const code = searchParams.get('code')
  const [secureLoginUrl, setSecureLoginUrl] = useState<string>('')
  const [pkce_code_verifier, setPkce_code_verifier] = useState<string>('')
  const [accessToken, setAccessToken] = useState<string>('')
  const [refreshToken, setRefreshToken] = useState<string>('')
  const [idToken, setIdToken] = useState<string>('')
  const isAuthenticated = useAppSelector(
    (state: RootState) => state.session.isAuthenticated
  )

  // PKCE utility functions
  const generateRandomString = (length: number) => {
    const array = new Uint8Array(length)
    window.crypto.getRandomValues(array)
    return Array.from(array, (dec) => ('0' + dec.toString(16)).slice(-2)).join(
      ''
    )
  }

  const sha256base64url = async (str: string) => {
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    const hash = await window.crypto.subtle.digest('SHA-256', data)
    const bytes = new Uint8Array(hash)
    const base64 = window.btoa(String.fromCharCode(...bytes))
    // Convert to base64url
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  }

  // Generate PKCE code_verifier and code_challenge for login URL
  useEffect(() => {
    if (!pkce_code_verifier && !code) {
      ;(async () => {
        const code_verifier = generateRandomString(64)
        localStorage.setItem('pkce_code_verifier', code_verifier)
        const code_challenge = await sha256base64url(code_verifier)
        const url = `${loginURL}/login?client_id=${loginClientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${redirectUri}&code_challenge=${code_challenge}&code_challenge_method=S256`
        setSecureLoginUrl(url)
        setPkce_code_verifier(code_verifier)
      })()
    }
  }, [pkce_code_verifier, code])

  useEffect(() => {
    // if refresh login code is we need to retreive other creds i.e refresh token, grant etc
    const exchangeCodeForToken = async () => {
      try {
        // TODO: Replace this with the actual code_verifier from PKCE flow
        const code_verifier =
          localStorage.getItem('pkce_code_verifier') ?? 'YOUR_CODE_VERIFIER'
        const params = new URLSearchParams()
        params.append('grant_type', 'authorization_code')
        params.append('client_id', loginClientId)
        params.append('code', code!)
        params.append('redirect_uri', window.location.origin)
        params.append('code_verifier', code_verifier)
        const response = await fetch(`${loginURL}/oauth2/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString(),
        })
        if (!response.ok) {
          const err = await response.json().catch(() => ({}))
          console.error('exchangeCodeForToken error', err)
          setLoginError('Failed to exchange code for token')
        }
        const data = await response.json()
        if (data.access_token) setAccessToken(data.access_token)
        if (data.id_token) setIdToken(data.id_token)
        if (data.refresh_token) setRefreshToken(data.refresh_token)
      } catch (e: unknown) {
        if (e instanceof Error) {
          console.error(e.message || 'Failed to exchange code for token')
        } else {
          console.error('Failed to exchange code for token')
        }
        setLoginError('Failed to exchange code for token')
      }
    }
    if (code) {
      exchangeCodeForToken()
    }
  }, [code, loginClientId, loginURL])

  if (isAuthenticated) {
    return <>{children}</>
  }
  if (accessToken && refreshToken && idToken) {
    return <LoginAttempt token={accessToken} text={'Authenticating...'} />
  } else if (code) {
    return <LoginAttempt token={''} text={'Authenticating Cognito...'} />
  } else if (!code && !pkce_code_verifier) {
    return <LoginAttempt token={''} text={'Securing browser information...'} />
  } else if (loginError) {
    return <>{loginError}</>
  } else {
    return <RedirectLoginUrl url={secureLoginUrl} />
  }
}

export default CognitoAuthGuard
